import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CalendarIcon, Save, Truck, Building2, Loader2, Trash2 } from "lucide-react";
import { format } from "date-fns";

export default function DailyCaptureSheet({ open, onOpenChange, onSaved, api, vehicles }) {
  const [date, setDate] = useState(new Date());
  const [vehicleRevenues, setVehicleRevenues] = useState({});
  const [vehicleDataIds, setVehicleDataIds] = useState({}); // Track IDs for deletion
  const [companyTurnover, setCompanyTurnover] = useState("");
  const [companyTurnoverId, setCompanyTurnoverId] = useState(null); // Track company turnover ID
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Fetch existing data when date changes or sheet opens
  const fetchExistingData = useCallback(async (selectedDate) => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${api}/daily-capture/${dateStr}`);
      const data = response.data;
      
      // Populate vehicle revenues and track IDs
      const revenues = {};
      const dataIds = {};
      vehicles.forEach((v) => {
        revenues[v.vehicle_name] = data.vehicle_revenues[v.vehicle_name]?.toString() || "";
        if (data.vehicle_data_ids && data.vehicle_data_ids[v.vehicle_name]) {
          dataIds[v.vehicle_name] = data.vehicle_data_ids[v.vehicle_name];
        }
      });
      setVehicleRevenues(revenues);
      setVehicleDataIds(dataIds);
      
      // Populate company turnover and ID
      setCompanyTurnover(data.company_turnover > 0 ? data.company_turnover.toString() : "");
      setCompanyTurnoverId(data.company_turnover_id || null);
    } catch (error) {
      console.error("Error fetching existing data:", error);
      // Initialize empty if no data exists
      const initial = {};
      vehicles.forEach((v) => {
        initial[v.vehicle_name] = "";
      });
      setVehicleRevenues(initial);
      setVehicleDataIds({});
      setCompanyTurnover("");
      setCompanyTurnoverId(null);
    } finally {
      setLoading(false);
    }
  }, [api, vehicles]);

  // Fetch data when sheet opens - reset to today's date
  useEffect(() => {
    if (open && vehicles.length > 0) {
      const today = new Date();
      setDate(today);
      fetchExistingData(today);
    }
  }, [open, vehicles.length, fetchExistingData]);

  // Fetch data when date changes (user selects different date)
  const handleDateSelect = useCallback((newDate) => {
    if (newDate) {
      setDate(newDate);
      fetchExistingData(newDate);
      setCalendarOpen(false);
    }
  }, [fetchExistingData]);

  // Initialize vehicle revenues when vehicles change (but only if not loading)
  useEffect(() => {
    if (!loading && vehicles.length > 0 && Object.keys(vehicleRevenues).length === 0) {
      const initial = {};
      vehicles.forEach((v) => {
        initial[v.vehicle_name] = "";
      });
      setVehicleRevenues(initial);
    }
  }, [vehicles, loading, vehicleRevenues]);

  const handleVehicleChange = (vehicleName, value) => {
    setVehicleRevenues((prev) => ({
      ...prev,
      [vehicleName]: value,
    }));
  };

  const handleDeleteVehicleData = async (vehicleName) => {
    const dataId = vehicleDataIds[vehicleName];
    if (!dataId) {
      // No saved data to delete, just clear the field
      setVehicleRevenues((prev) => ({ ...prev, [vehicleName]: "" }));
      return;
    }

    try {
      await axios.delete(`${api}/vehicle-data/${dataId}`);
      setVehicleRevenues((prev) => ({ ...prev, [vehicleName]: "" }));
      setVehicleDataIds((prev) => {
        const updated = { ...prev };
        delete updated[vehicleName];
        return updated;
      });
      toast.success(`${vehicleName} data deleted`);
      onSaved(); // Refresh dashboard
    } catch (error) {
      console.error("Error deleting vehicle data:", error);
      toast.error("Failed to delete data");
    }
  };

  const handleDeleteCompanyTurnover = async () => {
    if (!companyTurnoverId) {
      // No saved data to delete, just clear the field
      setCompanyTurnover("");
      return;
    }

    try {
      await axios.delete(`${api}/company-turnover/${companyTurnoverId}`);
      setCompanyTurnover("");
      setCompanyTurnoverId(null);
      toast.success("Company turnover deleted");
      onSaved(); // Refresh dashboard
    } catch (error) {
      console.error("Error deleting company turnover:", error);
      toast.error("Failed to delete data");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      
      // Convert string values to numbers, filter out empty/zero
      const revenues = {};
      Object.entries(vehicleRevenues).forEach(([name, value]) => {
        const num = parseFloat(value) || 0;
        if (num > 0) {
          revenues[name] = num;
        }
      });

      await axios.post(`${api}/daily-capture`, {
        date: dateStr,
        vehicle_revenues: revenues,
        company_turnover: parseFloat(companyTurnover) || 0,
      });
      
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data");
    } finally {
      setSaving(false);
    }
  };

  const totalVehicleRevenue = Object.values(vehicleRevenues).reduce(
    (sum, val) => sum + (parseFloat(val) || 0),
    0
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white border-l border-[#E2E8F0] overflow-y-auto" data-testid="daily-capture-form">
        <SheetHeader>
          <SheetTitle className="text-[#0F172A] text-xl">Daily Capture</SheetTitle>
          <SheetDescription className="text-[#64748B]">
            Record daily revenue for all vehicles
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Date Picker */}
          <div className="space-y-2">
            <Label className="text-[#64748B]">Date</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-[#64748B]" />
                  {format(date, "PPP")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-[#E2E8F0]" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  initialFocus
                  className="bg-white text-[#0F172A]"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Vehicle Revenue Fields */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#64748B]">
              <Truck className="w-4 h-4" />
              <Label>Vehicle Revenue</Label>
              {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
            
            {vehicles.length === 0 ? (
              <p className="text-[#94A3B8] text-sm">No vehicles configured. Add vehicles in Settings first.</p>
            ) : (
              <div className="space-y-3">
                {vehicles.map((vehicle) => (
                  <div key={vehicle.vehicle_name} className="flex items-center gap-2">
                    <Label className="text-[#0F172A] w-24 flex-shrink-0 text-sm">{vehicle.vehicle_name}</Label>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">R</span>
                      <Input
                        type="number"
                        value={vehicleRevenues[vehicle.vehicle_name] || ""}
                        onChange={(e) => handleVehicleChange(vehicle.vehicle_name, e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                        data-testid={`revenue-${vehicle.vehicle_name.toLowerCase()}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteVehicleData(vehicle.vehicle_name)}
                      className="h-9 w-9 text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#DC2626]/10 flex-shrink-0"
                      data-testid={`delete-${vehicle.vehicle_name.toLowerCase()}`}
                      disabled={!vehicleRevenues[vehicle.vehicle_name] && !vehicleDataIds[vehicle.vehicle_name]}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Vehicle Total */}
            {vehicles.length > 0 && (
              <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
                <span className="text-[#64748B] text-sm">Vehicle Total</span>
                <span className="text-[#0F172A] font-semibold">
                  R {totalVehicleRevenue.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Company Turnover */}
          <div className="space-y-2 pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-2 text-[#64748B]">
              <Building2 className="w-4 h-4" />
              <Label>Company Turnover</Label>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">R</span>
                <Input
                  type="number"
                  value={companyTurnover}
                  onChange={(e) => setCompanyTurnover(e.target.value)}
                  placeholder="Enter total company turnover"
                  className="pl-7 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                  data-testid="company-turnover-input"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDeleteCompanyTurnover}
                className="h-9 w-9 text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#DC2626]/10 flex-shrink-0"
                data-testid="delete-company-turnover"
                disabled={!companyTurnover && !companyTurnoverId}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[#94A3B8] text-xs">Optional: Record overall company turnover for the day</p>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#0891B2] hover:bg-[#0E7490] text-white mt-4"
            data-testid="save-entry-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving..." : "Save Daily Capture"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
